// Story 11.4 — Topbar del editor: status del bot, indicador unsaved, acciones
// Deploy/Rollback/DevMode + menu `[…]` con presets de layout.

import { ChevronDown, MoreHorizontal, Rocket, Terminal, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useWsEvent } from '@/hooks/use-ws-event';
import type { BotRowSummary } from '@/lib/api-client';
import type { LayoutPreset } from '@/lib/editor-diagram-prefs';
import { isDirty } from '@/lib/editor-tabs-reducer';
import { cn } from '@/lib/utils';
import { useEditorStore } from '@/stores/editor-store';
import { BotStatusBadge } from '../bots/BotStatusBadge';
import { ValidationStatusIndicator } from './ValidationStatusIndicator';

interface Props {
  bot: BotRowSummary | undefined;
  onDeploy: () => void;
  onRollback: () => void;
  onDevModeInfo: () => void;
  onApplyLayout: (preset: LayoutPreset) => void;
  /** Story 11.5 — abre el LogPanel filtrado a errores de validación. */
  onShowValidationErrors?: () => void;
  /** Story 13.x — abre el dialog "Guardar como template". */
  onSaveAsTemplate?: () => void;
}

const DEV_MODE_PULSE_MS = 30_000;

export function EditorToolbar({
  bot,
  onDeploy,
  onRollback,
  onDevModeInfo,
  onApplyLayout,
  onShowValidationErrors,
  onSaveAsTemplate,
}: Props): React.ReactElement {
  const tabs = useEditorStore((s) => s.tabs);
  const dirtyCount = tabs.filter(isDirty).length;
  const [menuOpen, setMenuOpen] = useState(false);
  const [devModeUntil, setDevModeUntil] = useState<number | null>(null);

  // Heurística dev-mode: vimos `bot:started` para este bot recientemente.
  // P-440 (review 11.4): el backend siempre emite `botId` numérico
  // stringified (Story 6.3 / P-233 ya documentado en 10.2). La rama
  // `env.botId !== bot.name` era dead code — la quitamos para no esconder
  // bugs si en el futuro algún emit cambia formato.
  useWsEvent('bot:started', (envelope) => {
    if (!bot) return;
    const env = envelope as unknown as { botId?: string | number };
    if (env.botId == null || String(env.botId) !== String(bot.id)) return;
    setDevModeUntil(Date.now() + DEV_MODE_PULSE_MS);
  });
  useEffect(() => {
    if (devModeUntil == null) return;
    const remaining = devModeUntil - Date.now();
    if (remaining <= 0) {
      setDevModeUntil(null);
      return;
    }
    const t = window.setTimeout(() => setDevModeUntil(null), remaining);
    return () => window.clearTimeout(t);
  }, [devModeUntil]);

  const devModeActive = devModeUntil != null && devModeUntil > Date.now();
  const status = bot?.status ?? 'inactive';

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card/40 px-3 py-1.5">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{bot?.name ?? '…'}</span>
        <BotStatusBadge
          status={status}
          pulse={devModeActive && status === 'active'}
          className={cn(devModeActive && status === 'active' && 'border-success/60')}
        />
        {devModeActive && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-success/40 bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success"
            title="WS detectó bot:started recientemente"
          >
            DEV
          </span>
        )}
        {dirtyCount > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
            title={`${dirtyCount} archivo${dirtyCount === 1 ? '' : 's'} con cambios sin guardar`}
          >
            ● {dirtyCount} sin guardar
          </span>
        )}
        {/* Story 11.5 — indicador de validación. */}
        <ValidationStatusIndicator onClick={onShowValidationErrors} />
      </div>
      <div className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={onDevModeInfo} title="Cómo lanzar dev mode">
          <Terminal className="h-3.5 w-3.5" /> Dev Mode
        </Button>
        <Button size="sm" variant="default" onClick={onDeploy} disabled={!bot}>
          <Rocket className="h-3.5 w-3.5" /> Deploy
        </Button>
        <Button size="sm" variant="outline" onClick={onRollback} disabled={!bot}>
          <Undo2 className="h-3.5 w-3.5" /> Rollback
        </Button>
        <div className="relative">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Más acciones"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="h-7 w-7"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-10 mt-1 min-w-[180px] overflow-hidden rounded-md border border-border bg-popover text-xs shadow-lg"
              onMouseLeave={() => setMenuOpen(false)}
            >
              {(
                [
                  { key: 'split', label: 'Layout: Split' },
                  { key: 'code-only', label: 'Layout: Code only' },
                  { key: 'code-diagram', label: 'Layout: Code + Diagram' },
                  { key: 'diagram-expanded', label: 'Layout: Diagram expanded' },
                ] as const
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onApplyLayout(item.key as LayoutPreset);
                    setMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-1 px-3 py-1.5 text-left hover:bg-accent hover:text-accent-foreground"
                >
                  <ChevronDown className="h-3 w-3 opacity-0" aria-hidden />
                  {item.label}
                </button>
              ))}
              {onSaveAsTemplate && (
                <>
                  <div className="my-1 border-t border-border" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onSaveAsTemplate();
                      setMenuOpen(false);
                    }}
                    disabled={!bot}
                    className="flex w-full items-center gap-1 px-3 py-1.5 text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                  >
                    <ChevronDown className="h-3 w-3 opacity-0" aria-hidden />
                    Guardar como template…
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
